import { useEffect, useRef, useState } from 'react';
import { Search, BarChart3, Clock, MessageSquare } from 'lucide-react';
import { InvestigatorDemo } from './product-tour/InvestigatorDemo';
import { InvestigatorFlow } from './InvestigatorFlow';
import { useT } from '@/i18n/provider';
import { cn } from '@/lib/utils';

// Decoratieve icon-tegels uniform in de primary-familie: kleur zit in de
// semantiek van de demo (rood=error, groen=klaar), niet in categorielabels.
const capabilities = [
  { key: 'search', icon: Search },
  { key: 'patterns', icon: BarChart3 },
  { key: 'compare', icon: Clock },
  { key: 'followUp', icon: MessageSquare },
] as const;

export function AIAgentSection() {
  const t = useT();
  const sectionRef = useRef<HTMLElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="ai-agent" className="py-24 sm:py-32 gradient-subtle">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-2xl mx-auto text-center mb-16 sm:mb-20 animate-on-scroll">
          <p className="eyebrow">{t('aiAgent.badge')}</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 dark:text-white text-balance">
            {t('aiAgent.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 text-balance">
            {t('aiAgent.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Animated AI Investigator demo */}
          <div className="animate-on-scroll order-2 lg:order-1">
            <InvestigatorDemo enabled={isInView} />
          </div>

          {/* Right: capabilities */}
          <div className="order-1 lg:order-2">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 animate-on-scroll">
              {t('aiAgent.capabilitiesTitle')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8 animate-on-scroll">
              {t('aiAgent.capabilitiesSubtitle')}
            </p>

            <div className="space-y-4">
              {capabilities.map((cap, i) => {
                const Icon = cap.icon;
                return (
                  <div
                    key={cap.key}
                    className="animate-on-scroll flex items-start gap-4 p-4 rounded-xl glass-subtle card-hover"
                    style={{ transitionDelay: `${i * 100}ms` }}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-primary-50 dark:bg-primary-500/10">
                      <Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-gray-900 dark:text-white">
                        {t(`aiAgent.cap.${cap.key}.title`)}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        {t(`aiAgent.cap.${cap.key}.description`)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Effort levels */}
            <div className="mt-8 animate-on-scroll">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                {t('aiAgent.effortTitle')}
              </p>
              <div className="flex gap-2">
                {(['fast', 'balanced', 'deep'] as const).map((level) => (
                  <div
                    key={level}
                    className={cn(
                      'flex-1 text-center px-3 py-2 rounded-lg border text-xs font-medium',
                      level === 'balanced'
                        ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'
                    )}
                  >
                    {t(`aiAgent.effort.${level}`)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <InvestigatorFlow />
      </div>
    </section>
  );
}
